import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminLogin as apiLogin, getCurrentAdmin } from '../services/api';
import {
  ADMIN_SESSION_EXPIRED_EVENT,
  ADMIN_SESSION_EXPIRED_MESSAGE,
  clearAdminToken,
  getAdminToken,
  redirectToAdminLogin,
  setAdminToken,
} from '../services/adminSession';

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expiredSessionWarning, setExpiredSessionWarning] = useState(null);

  useEffect(() => {
    const handleExpiredSession = (event) => {
      clearAdminToken();
      if (event.detail?.preserveWork) {
        event.preventDefault();
        setExpiredSessionWarning(
          event.detail?.message || ADMIN_SESSION_EXPIRED_MESSAGE
        );
        return;
      }
      setAdmin(null);
    };

    window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleExpiredSession);
    return () => {
      window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleExpiredSession);
    };
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getAdminToken();
      if (token) {
        try {
          const adminData = await getCurrentAdmin();
          setAdmin(adminData);
        } catch (err) {
          // Token invalid/expired
          clearAdminToken();
          setAdmin(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (username, password) => {
    setError(null);
    try {
      const response = await apiLogin(username, password);
      setAdminToken(response.access_token);
      setAdmin(response.user);
      return true;
    } catch (err) {
      // Handle different error types for better user feedback
      let message = 'Login failed. Please try again.';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        message = 'Connection timed out. Please check your internet connection and try again.';
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        message = 'Network error. Please check your internet connection.';
      } else if (err.response?.data?.detail) {
        message = err.response.data.detail;
      } else if (err.response?.status === 401) {
        message = 'Invalid username or password.';
      } else if (err.response?.status >= 500) {
        message = 'Server error. Please try again later.';
      }
      
      setError(message);
      return false;
    }
  };

  const logout = () => {
    clearAdminToken();
    setAdmin(null);
  };

  const continueToLogin = () => {
    setExpiredSessionWarning(null);
    setAdmin(null);
    redirectToAdminLogin();
  };

  const value = {
    admin,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!admin,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
      {expiredSessionWarning && (
        <div
          className="fixed bottom-4 left-4 right-4 z-[1000] mx-auto max-w-2xl rounded-xl border border-amber-400/40 bg-[#17130a] p-4 text-left shadow-2xl"
          role="alert"
          data-testid="admin-session-expired-warning"
        >
          <p className="font-semibold text-amber-200">{expiredSessionWarning}</p>
          <p className="mt-1 text-sm text-amber-100/80">
            Kaydedilmemiş değişiklikleriniz varsa giriş sayfasına geçmeden önce
            kopyalayın. Bu sayfadaki veriler siz ayrılana kadar ekranda kalacak.
          </p>
          <button
            type="button"
            onClick={continueToLogin}
            className="mt-3 rounded-lg bg-amber-300 px-4 py-2 text-sm font-semibold text-black"
          >
            Giriş sayfasına git
          </button>
        </div>
      )}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;
